import { pick, uniq, values } from 'lodash-es'
import { objectEntries, objectFromEntries } from '#app/lib/utils'
import { authorRoleProperties } from '#entities/lib/editor/properties_per_subtype'
import { getEntitiesAttributesByUris, serializeEntities, type SerializedEntitiesByUris, type SerializedEntity } from '#entities/lib/entities'
import { getEditionAuthorsUris } from '#entities/lib/types/edition'
import { getWorksAuthorsUris, getWorksSeriesUris } from '#entities/lib/types/work_alt'
import type { EntityUri } from '#server/types/entity'
import { getCurrentLang } from '#user/lib/i18n'

const relatives = [
  'wdt:P123',
  'wdt:P195',
  'wdt:P179',
  'wdt:P629',
  ...authorRoleProperties,
] as const

export async function getItemEntityData (uri: EntityUri) {
  const { entities } = await getEntitiesAttributesByUris({
    uris: [ uri ],
    attributes: [ 'info', 'labels', 'claims', 'image' ],
    lang: getCurrentLang(),
    relatives,
  })
  const entity = entities[uri]
  const edition = entity.type === 'edition' ? entity : null
  let works: SerializedEntity[]
  let publishers: SerializedEntity[]
  let collections: SerializedEntity[]
  if (edition) {
    works = values(pick(entities, entity.claims['wdt:P629']))
    publishers = values(pick(entities, entity.claims['wdt:P123']))
    collections = values(pick(entities, entity.claims['wdt:P195']))
  } else if (entity.type === 'work') {
    // Legacy items might link directly to a work entity
    works = [ entity ]
  }
  const seriesUris = getWorksSeriesUris(works)
  const editionAuthorsUris = edition ? getEditionAuthorsUris(entity) : []
  const workAuthorsUris = getWorksAuthorsUris(works)
  const series = values(pick(entities, seriesUris))
  const authorsByUris = pick(entities, [ ...workAuthorsUris, ...editionAuthorsUris ])
  const authorsByProperty = await getAuthorsByProperty(edition, works, authorsByUris)
  return { entity, works, series, authorsByProperty, publishers, collections }
}

async function getAuthorsByProperty (edition: SerializedEntity | null, works: SerializedEntity[], authorsByUris: SerializedEntitiesByUris) {
  const authorsByProperty = objectFromEntries(authorRoleProperties.map(property => {
    return [
      property,
      [] as SerializedEntity[],
    ]
  }))

  for (const entity of [ edition, ...works ]) {
    for (const property of authorRoleProperties) {
      const propertyAuthorsUris = entity.claims[property]
      if (propertyAuthorsUris) {
        const propertyAuthors = values(pick(authorsByUris, propertyAuthorsUris))
        authorsByProperty[property].push(...propertyAuthors)
      }
    }
  }

  for (const [ property, authors ] of objectEntries(authorsByProperty)) {
    authorsByProperty[property] = await serializeEntities(uniq(authors))
  }

  return authorsByProperty
}
